<script>
  import { createEventDispatcher, onMount } from "svelte";
  import { fade, slide } from "svelte/transition";
  import PlayFilled from "carbon-icons-svelte/lib/PlayFilled.svelte";
  import Close from "carbon-icons-svelte/lib/Close.svelte";
  import Calendar from "carbon-icons-svelte/lib/Calendar.svelte";
  import DeliveryTruck from "carbon-icons-svelte/lib/DeliveryTruck.svelte";
  import WarningFilled from "carbon-icons-svelte/lib/WarningFilled.svelte";
  import User from "carbon-icons-svelte/lib/User.svelte";
  import Enterprise from "carbon-icons-svelte/lib/Enterprise.svelte";
  import Email from "carbon-icons-svelte/lib/Email.svelte";
  import Phone from "carbon-icons-svelte/lib/Phone.svelte";
  import Send from "carbon-icons-svelte/lib/Send.svelte";
  import { formatPhoneNumber, toE164 } from "../../phoneUtils.js";
  import { fetchCompanies, fetchYards } from "../../data";
  import { getJobCustomerName, getQuoteCustomerName } from "./jobMaterialUtils.js";

  export let isOpen = false;
  export let currentJob = null;
  export let selectedQuote = null;
  export let currentAllocations = null;
  export let initialStartDate = "";
  export let isStartingJob = false;
  export let startJobError = "";
  export let cachedCompanies = [];
  export let cachedYards = [];

  const dispatch = createEventDispatcher();

  let startJobDate = "";
  let startJobMainContacts = [];
  let startJobSecondaryContacts = [];
  let startJobDrivers = [];
  let isLoadingModalContacts = false;

  $: if (isOpen) {
    initModal();
  }

  async function ensureCompaniesLoaded() {
    if (!cachedCompanies || cachedCompanies.length === 0) {
      try {
        cachedCompanies = await fetchCompanies();
      } catch (e) {
        console.error("Failed to load companies for start job modal:", e);
      }
    }
  }

  async function ensureYardsLoaded() {
    if (!cachedYards || cachedYards.length === 0) {
      try {
        cachedYards = await fetchYards();
      } catch (e) {
        console.error("Failed to load yards for start job modal:", e);
      }
    }
  }

  function isContactDuplicate(candidate, list) {
    if (!candidate || !candidate.name) return false;
    const cName = candidate.name.trim().toLowerCase();
    const cPhone = candidate.phone ? candidate.phone.replace(/\D/g, "") : "";
    const cEmail = candidate.email ? candidate.email.trim().toLowerCase() : "";

    return list.some((item) => {
      const iName = (item.name || "").trim().toLowerCase();
      const iPhone = item.phone ? item.phone.replace(/\D/g, "") : "";
      const iEmail = item.email ? item.email.trim().toLowerCase() : "";
      if (cName === iName) return true;
      if (cPhone && iPhone && cPhone === iPhone) return true;
      if (cEmail && iEmail && cEmail === iEmail) return true;
      return false;
    });
  }

  async function initModal() {
    startJobDate = currentJob?.start_date
      ? currentJob.start_date.split("T")[0]
      : (initialStartDate || new Date().toISOString().split("T")[0]);

    await buildStartJobContacts();
  }

  async function buildStartJobContacts() {
    isLoadingModalContacts = true;
    startJobError = "";

    await Promise.all([ensureCompaniesLoaded(), ensureYardsLoaded()]);

    const mainList = [];
    const secList = [];

    // --- 1. Main Contacts: 1. The person it was created for ---
    let personCreatedFor = null;
    const qContact = selectedQuote?.contact;
    if (qContact && qContact.name && qContact.name.trim()) {
      personCreatedFor = {
        name: qContact.name.trim(),
        phone: qContact.phone || selectedQuote?.customer_summary?.phone || (typeof selectedQuote?.customer === 'object' ? selectedQuote?.customer?.phone : "") || "",
        email: qContact.email || selectedQuote?.customer_summary?.email || (typeof selectedQuote?.customer === 'object' ? selectedQuote?.customer?.email : "") || "",
        position: qContact.position || "Client Representative",
        sourceLabel: selectedQuote?.customer_summary?.name || (typeof selectedQuote?.customer === 'object' ? selectedQuote?.customer?.name : selectedQuote?.customer) || "Client",
        roleBadge: "Person Created For"
      };
    } else if (selectedQuote?.customer?.contact_person && selectedQuote.customer.contact_person.trim()) {
      personCreatedFor = {
        name: selectedQuote.customer.contact_person.trim(),
        phone: selectedQuote.customer.phone || "",
        email: selectedQuote.customer.email || "",
        position: "Client Representative",
        sourceLabel: selectedQuote.customer.name || "Client",
        roleBadge: "Person Created For"
      };
    } else if (currentJob?.customer?.contact_person && currentJob.customer.contact_person.trim()) {
      personCreatedFor = {
        name: currentJob.customer.contact_person.trim(),
        phone: currentJob.customer.phone || "",
        email: currentJob.customer.email || "",
        position: "Client Representative",
        sourceLabel: currentJob.customer.name || "Client",
        roleBadge: "Person Created For"
      };
    } else if (currentJob?.main_contact && currentJob.main_contact.name && currentJob.main_contact.name.trim()) {
      personCreatedFor = {
        name: currentJob.main_contact.name.trim(),
        phone: currentJob.main_contact.phone || "",
        email: currentJob.main_contact.email || "",
        position: currentJob.main_contact.position || "Site Client",
        sourceLabel: currentJob.customer?.name || "Client",
        roleBadge: "Person Created For"
      };
    }

    if (personCreatedFor && !isContactDuplicate(personCreatedFor, mainList)) {
      mainList.push(personCreatedFor);
    }

    // --- 1. Main Contacts: 2. Everyone in the Job.contacts list ---
    const rawJobContacts = (currentJob?.contacts && currentJob.contacts.length > 0)
      ? currentJob.contacts
      : (selectedQuote?.contacts || []);

    for (const c of rawJobContacts) {
      if (!c || !c.name || !c.name.trim()) continue;
      const candidate = {
        name: c.name.trim(),
        phone: c.phone || "",
        email: c.email || "",
        position: c.position || "Site Contact",
        sourceLabel: currentJob?.customer?.name || selectedQuote?.customer?.name || "Job Site",
        roleBadge: c.position || "Job Contact"
      };
      if (!isContactDuplicate(candidate, mainList)) {
        mainList.push(candidate);
      }
    }

    // --- 1. Main Contacts: 3. The Yards main contact ---
    const mats = (currentJob?.materials && currentJob.materials.length > 0)
      ? currentJob.materials
      : (selectedQuote?.quote_materials || []);

    const involvedYardIds = new Set();
    const involvedYardNames = new Set();
    mats.forEach((m) => {
      if (m.yard?.id != null) involvedYardIds.add(String(m.yard.id));
      if (m.yard_id != null) involvedYardIds.add(String(m.yard_id));
      if (m.yard?.name) involvedYardNames.add(m.yard.name.trim().toLowerCase());
      if (m.yard_name) involvedYardNames.add(m.yard_name.trim().toLowerCase());
    });

    const matchedYards = cachedYards.filter((y) =>
      involvedYardIds.has(String(y.id)) ||
      involvedYardNames.has((y.name || "").trim().toLowerCase())
    );

    for (const y of matchedYards) {
      const yContacts = Array.isArray(y.contacts) ? y.contacts : [];
      const mainYContact = yContacts.find((c) => c.main) || yContacts[0];
      if (mainYContact && mainYContact.name && mainYContact.name.trim()) {
        const candidate = {
          name: mainYContact.name.trim(),
          phone: mainYContact.phone || y.phone || "",
          email: mainYContact.email || "",
          position: mainYContact.position || "Weighbridge Clerk",
          sourceLabel: y.name || "Disposal Yard",
          roleBadge: "Yard Main Contact"
        };
        if (!isContactDuplicate(candidate, mainList)) {
          mainList.push(candidate);
        }
      }
    }

    // Map main contacts to UI state with default: email = true (if email exists), phone = false
    startJobMainContacts = mainList.map((c, idx) => ({
      ...c,
      id: `main-${idx}-${Date.now()}`,
      sendEmail: Boolean(c.email && c.email.trim()),
      sendPhone: false
    }));

    // --- 2. Secondary Contacts: 1. All contacts of the Customer it was prepared for ---
    const targetCompId = (currentJob?.customer && typeof currentJob.customer === 'object' ? currentJob.customer.id : currentJob?.customer) || (selectedQuote?.customer && typeof selectedQuote.customer === 'object' ? selectedQuote.customer.id : selectedQuote?.customer);
    const targetCompName = ((currentJob?.customer && typeof currentJob.customer === 'object' ? currentJob.customer.name : currentJob?.customer) || selectedQuote?.customer_summary?.name || (selectedQuote?.customer && typeof selectedQuote.customer === 'object' ? selectedQuote.customer.name : selectedQuote?.customer) || "").trim().toLowerCase();

    const matchedComp = cachedCompanies.find((comp) =>
      (targetCompId && String(comp.id) === String(targetCompId)) ||
      (comp.name && comp.name.trim().toLowerCase() === targetCompName)
    );

    if (matchedComp && Array.isArray(matchedComp.contacts)) {
      for (const c of matchedComp.contacts) {
        if (!c || !c.name || !c.name.trim()) continue;
        const candidate = {
          name: c.name.trim(),
          phone: c.phone || matchedComp.phone || "",
          email: c.email || "",
          position: c.position || "Company Representative",
          sourceLabel: matchedComp.name || "Client Company",
          roleBadge: "Company Contact"
        };
        if (!isContactDuplicate(candidate, mainList) && !isContactDuplicate(candidate, secList)) {
          secList.push(candidate);
        }
      }
    }

    // --- 2. Secondary Contacts: 2. All Yard company contacts ---
    for (const y of matchedYards) {
      const yContacts = Array.isArray(y.contacts) ? y.contacts : [];
      for (const c of yContacts) {
        if (!c || !c.name || !c.name.trim()) continue;
        const candidate = {
          name: c.name.trim(),
          phone: c.phone || y.phone || "",
          email: c.email || "",
          position: c.position || "Yard Operations",
          sourceLabel: y.name || "Disposal Yard",
          roleBadge: "Yard Contact"
        };
        if (!isContactDuplicate(candidate, mainList) && !isContactDuplicate(candidate, secList)) {
          secList.push(candidate);
        }
      }
    }

    // Map secondary contacts to UI state with default: email = false, phone = false
    startJobSecondaryContacts = secList.map((c, idx) => ({
      ...c,
      id: `sec-${idx}-${Date.now()}`,
      sendEmail: false,
      sendPhone: false
    }));

    // --- 3. Allocated Fleet Drivers ---
    const driversMap = new Map();

    // From currentAllocations
    if (currentAllocations) {
      Object.values(currentAllocations).forEach((allocEntry) => {
        if (allocEntry && allocEntry.days) {
          Object.entries(allocEntry.days).forEach(([dayIdx, list]) => {
            if (Array.isArray(list)) {
              list.forEach((item) => {
                const drv = item.driver || item.truck?.default_driver || {};
                const dName = (drv.name || "").trim();
                const trkId = item.truck?.id || (typeof item.truck === "string" ? item.truck : "");
                const trlId = item.trailer?.id || item.truck?.default_trailer?.id || item.truck?.trailer?.id || "";
                const dayNum = parseInt(dayIdx, 10) + 1;
                const shiftStr = `Day ${dayNum}${item.scheduledStartTime ? ` @ ${item.scheduledStartTime}` : ""}`;

                if (dName || trkId) {
                  const key = (dName || trkId).toLowerCase();
                  if (driversMap.has(key)) {
                    const existing = driversMap.get(key);
                    if (!existing.shifts.includes(shiftStr)) existing.shifts.push(shiftStr);
                    if (trkId && !existing.trucks.includes(trkId)) existing.trucks.push(trkId);
                  } else {
                    driversMap.set(key, {
                      id: `drv-${driversMap.size}-${Date.now()}`,
                      name: dName || `Driver (${trkId || 'Fleet'})`,
                      phone: drv.phone || "",
                      trucks: trkId ? [trkId] : [],
                      trailerId: trlId,
                      shifts: [shiftStr],
                      selected: true
                    });
                  }
                }
              });
            }
          });
        }
      });
    }

    // Also from currentJob?.materials allocated_trucks
    if (currentJob && Array.isArray(currentJob.materials)) {
      currentJob.materials.forEach((mat) => {
        if (Array.isArray(mat.allocated_trucks)) {
          mat.allocated_trucks.forEach((item) => {
            const drv = item.driver || item.truck?.default_driver || {};
            const dName = (drv.name || "").trim();
            const trkId = item.truck?.id || (typeof item.truck === "string" ? item.truck : "");
            const trlId = item.trailer?.id || item.truck?.default_trailer?.id || "";
            const dayNum = item.scheduled_day || 1;
            const shiftStr = `Day ${dayNum}${item.scheduled_start_time ? ` @ ${item.scheduled_start_time}` : ""}`;

            if (dName || trkId) {
              const key = (dName || trkId).toLowerCase();
              if (driversMap.has(key)) {
                const existing = driversMap.get(key);
                if (!existing.shifts.includes(shiftStr)) existing.shifts.push(shiftStr);
                if (trkId && !existing.trucks.includes(trkId)) existing.trucks.push(trkId);
              } else {
                driversMap.set(key, {
                  id: `drv-${driversMap.size}-${Date.now()}`,
                  name: dName || `Driver (${trkId || 'Fleet'})`,
                  phone: drv.phone || "",
                  trucks: trkId ? [trkId] : [],
                  trailerId: trlId,
                  shifts: [shiftStr],
                  selected: true
                });
              }
            }
          });
        }
      });
    }

    // Also from selectedQuote?.quote_materials allocated_trucks if unconverted
    if (!currentJob && selectedQuote && Array.isArray(selectedQuote.quote_materials)) {
      selectedQuote.quote_materials.forEach((mat) => {
        if (Array.isArray(mat.allocated_trucks)) {
          mat.allocated_trucks.forEach((item) => {
            const drv = item.driver || item.truck?.default_driver || {};
            const dName = (drv.name || "").trim();
            const trkId = item.truck?.id || (typeof item.truck === "string" ? item.truck : "");
            const trlId = item.trailer?.id || item.truck?.default_trailer?.id || "";
            const dayNum = item.scheduled_day || 1;
            const shiftStr = `Day ${dayNum}${item.scheduled_start_time ? ` @ ${item.scheduled_start_time}` : ""}`;

            if (dName || trkId) {
              const key = (dName || trkId).toLowerCase();
              if (driversMap.has(key)) {
                const existing = driversMap.get(key);
                if (!existing.shifts.includes(shiftStr)) existing.shifts.push(shiftStr);
                if (trkId && !existing.trucks.includes(trkId)) existing.trucks.push(trkId);
              } else {
                driversMap.set(key, {
                  id: `drv-${driversMap.size}-${Date.now()}`,
                  name: dName || `Driver (${trkId || 'Fleet'})`,
                  phone: drv.phone || "",
                  trucks: trkId ? [trkId] : [],
                  trailerId: trlId,
                  shifts: [shiftStr],
                  selected: true
                });
              }
            }
          });
        }
      });
    }

    startJobDrivers = Array.from(driversMap.values());
    isLoadingModalContacts = false;
  }

  function handleClose() {
    if (!isStartingJob) {
      dispatch("close");
    }
  }

  function handleConfirm() {
    if (!isStartingJob) {
      dispatch("confirm", {
        startDate: startJobDate,
        mainContacts: startJobMainContacts,
        secondaryContacts: startJobSecondaryContacts,
        drivers: startJobDrivers
      });
    }
  }
</script>

{#if isOpen}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div
    class="start-job-modal-backdrop"
    role="presentation"
    on:click={handleClose}
    transition:fade={{ duration: 150 }}
  >
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div
      class="start-job-modal-card"
      role="dialog"
      aria-modal="true"
      on:click|stopPropagation
      transition:slide={{ duration: 200 }}
    >
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="modal-title-group">
          <div class="modal-icon-badge">
            <PlayFilled size={20} />
          </div>
          <div>
            <h3 class="modal-title">ACTIVATE JOB &amp; DISPATCH BRIEFING</h3>
            <p class="modal-subtitle">
              Confirm commencement date, activate to live tracking, and select stakeholder notification recipients.
            </p>
          </div>
        </div>
        <button
          class="modal-close-icon-btn"
          type="button"
          on:click={handleClose}
          title="Close"
          disabled={isStartingJob}
        >
          <Close size={18} />
        </button>
      </div>

      <!-- Modal Body (Scrollable) -->
      <div class="modal-scroll-body">
        <!-- 1. Start Date Confirmation Bar -->
        <div class="modal-start-date-bar">
          <div class="date-field-col">
            <label class="modal-field-label" for="start-job-date-input">
              <Calendar size={14} />
              <span>CONFIRM COMMENCEMENT DATE</span>
            </label>
            <div class="date-input-wrap">
              <input
                id="start-job-date-input"
                type="date"
                class="modal-date-picker"
                bind:value={startJobDate}
              />
            </div>
          </div>

          <div class="modal-quick-meta">
            <div class="quick-meta-pill">
              <span class="meta-label">TARGET JOB</span>
              <span class="meta-val">#{currentJob?.__job_number__ || currentJob?.id || selectedQuote?.quote_number || selectedQuote?.id}</span>
            </div>
            <div class="quick-meta-pill">
              <span class="meta-label">CLIENT</span>
              <span class="meta-val" title={currentJob ? getJobCustomerName(currentJob, selectedQuote) : getQuoteCustomerName(selectedQuote)}>
                {currentJob ? getJobCustomerName(currentJob, selectedQuote) : getQuoteCustomerName(selectedQuote)}
              </span>
            </div>
            <div class="quick-meta-pill">
              <span class="meta-label">ACTIVE FLEET</span>
              <span class="meta-val">{startJobDrivers.length} Allocated Trucks</span>
            </div>
          </div>
        </div>

        <!-- Informational Purpose Note -->
        <div class="modal-info-note">
          <Send size={15} class="note-icon" />
          <div class="note-text">
            <strong>Purpose of this list:</strong> Selected stakeholders will be sent a live summary of the job and a direct link to the operations tracking page.
            <span class="note-subtext">Messaging dispatch logic is currently wired to a simulated placeholder service.</span>
          </div>
        </div>

        {#if isLoadingModalContacts}
          <div class="modal-loading-state">
            <div class="spinner"></div>
            <span>Gathering stakeholders, yard personnel, and allocated drivers...</span>
          </div>
        {:else}
          <!-- SECTION 1: MAIN CONTACTS -->
          <div class="contacts-section">
            <div class="contacts-section-header">
              <div class="section-title-wrap">
                <User size={16} class="section-icon main-icon" />
                <h4 class="section-heading">Main Contacts</h4>
                <span class="count-tag count-main">{startJobMainContacts.length}</span>
              </div>
              <span class="section-hint">Email selected by default</span>
            </div>

            {#if startJobMainContacts.length === 0}
              <div class="empty-contacts-box">
                <span>No primary contacts found for this job or customer profile.</span>
              </div>
            {:else}
              <div class="contacts-cards-list">
                {#each startJobMainContacts as contact (contact.id)}
                  <div class="contact-card main-contact-card">
                    <div class="contact-details">
                      <div class="contact-name-row">
                        <span class="contact-name">{contact.name}</span>
                        <span class="contact-badge badge-{contact.roleBadge === 'Yard Main Contact' ? 'yard' : (contact.roleBadge === 'Person Created For' ? 'created-for' : 'job')}">
                          {contact.roleBadge}
                        </span>
                      </div>
                      {#if contact.position || contact.sourceLabel}
                        <div class="contact-subtitle">
                          {contact.position ? contact.position : ''}
                          {contact.position && contact.sourceLabel ? ' • ' : ''}
                          {contact.sourceLabel ? contact.sourceLabel : ''}
                        </div>
                      {/if}
                      <div class="contact-comm-row">
                        <div class="comm-item {contact.phone ? '' : 'unavailable'}">
                          <Phone size={13} />
                          {#if contact.phone}
                            <a href="tel:{toE164(contact.phone)}" class="comm-link">{formatPhoneNumber(contact.phone)}</a>
                          {:else}
                            <span class="comm-na">No phone</span>
                          {/if}
                        </div>
                        <div class="comm-item {contact.email ? '' : 'unavailable'}">
                          <Email size={13} />
                          {#if contact.email}
                            <a href="mailto:{contact.email}" class="comm-link">{contact.email}</a>
                          {:else}
                            <span class="comm-na">No email</span>
                          {/if}
                        </div>
                      </div>
                    </div>

                    <div class="contact-channels">
                      <label class="channel-checkbox-label {contact.email ? '' : 'disabled'}" title={contact.email ? "Send summary via Email" : "No email address provided"}>
                        <input
                          type="checkbox"
                          class="channel-checkbox"
                          bind:checked={contact.sendEmail}
                          disabled={!contact.email}
                        />
                        <span class="channel-text">Email</span>
                      </label>
                      <label class="channel-checkbox-label {contact.phone ? '' : 'disabled'}" title={contact.phone ? "Send live link via SMS" : "No phone number provided"}>
                        <input
                          type="checkbox"
                          class="channel-checkbox"
                          bind:checked={contact.sendPhone}
                          disabled={!contact.phone}
                        />
                        <span class="channel-text">SMS</span>
                      </label>
                    </div>
                  </div>
                {/each}
              </div>
            {/if}
          </div>

          <!-- SECTION 2: SECONDARY CONTACTS -->
          <div class="contacts-section">
            <div class="contacts-section-header">
              <div class="section-title-wrap">
                <Enterprise size={16} class="section-icon secondary-icon" />
                <h4 class="section-heading">Secondary Contacts</h4>
                <span class="count-tag count-sec">{startJobSecondaryContacts.length}</span>
              </div>
              <span class="section-hint">Unselected by default • Excludes main contacts</span>
            </div>

            {#if startJobSecondaryContacts.length === 0}
              <div class="empty-contacts-box">
                <span>No additional company or yard contacts found.</span>
              </div>
            {:else}
              <div class="contacts-cards-list">
                {#each startJobSecondaryContacts as contact (contact.id)}
                  <div class="contact-card secondary-contact-card">
                    <div class="contact-details">
                      <div class="contact-name-row">
                        <span class="contact-name">{contact.name}</span>
                        <span class="contact-badge badge-{contact.roleBadge === 'Yard Contact' ? 'yard-sec' : 'company-sec'}">
                          {contact.roleBadge}
                        </span>
                      </div>
                      {#if contact.position || contact.sourceLabel}
                        <div class="contact-subtitle">
                          {contact.position ? contact.position : ''}
                          {contact.position && contact.sourceLabel ? ' • ' : ''}
                          {contact.sourceLabel ? contact.sourceLabel : ''}
                        </div>
                      {/if}
                      <div class="contact-comm-row">
                        <div class="comm-item {contact.phone ? '' : 'unavailable'}">
                          <Phone size={13} />
                          {#if contact.phone}
                            <a href="tel:{toE164(contact.phone)}" class="comm-link">{formatPhoneNumber(contact.phone)}</a>
                          {:else}
                            <span class="comm-na">No phone</span>
                          {/if}
                        </div>
                        <div class="comm-item {contact.email ? '' : 'unavailable'}">
                          <Email size={13} />
                          {#if contact.email}
                            <a href="mailto:{contact.email}" class="comm-link">{contact.email}</a>
                          {:else}
                            <span class="comm-na">No email</span>
                          {/if}
                        </div>
                      </div>
                    </div>

                    <div class="contact-channels">
                      <label class="channel-checkbox-label {contact.email ? '' : 'disabled'}" title={contact.email ? "Send summary via Email" : "No email address provided"}>
                        <input
                          type="checkbox"
                          class="channel-checkbox"
                          bind:checked={contact.sendEmail}
                          disabled={!contact.email}
                        />
                        <span class="channel-text">Email</span>
                      </label>
                      <label class="channel-checkbox-label {contact.phone ? '' : 'disabled'}" title={contact.phone ? "Send live link via SMS" : "No phone number provided"}>
                        <input
                          type="checkbox"
                          class="channel-checkbox"
                          bind:checked={contact.sendPhone}
                          disabled={!contact.phone}
                        />
                        <span class="channel-text">SMS</span>
                      </label>
                    </div>
                  </div>
                {/each}
              </div>
            {/if}
          </div>

          <!-- SECTION 3: ALLOCATED DRIVERS -->
          <div class="contacts-section">
            <div class="contacts-section-header">
              <div class="section-title-wrap">
                <DeliveryTruck size={16} class="section-icon driver-icon" />
                <h4 class="section-heading">Allocated Fleet Drivers</h4>
                <span class="count-tag count-drv">{startJobDrivers.length}</span>
              </div>
              <span class="section-hint">Default: All drivers selected</span>
            </div>

            {#if startJobDrivers.length === 0}
              <div class="empty-contacts-box warning-box">
                <WarningFilled size={16} />
                <span>No drivers currently assigned to this job's fleet. You can still start the job and assign vehicles on the allocation board.</span>
              </div>
            {:else}
              <div class="contacts-cards-list">
                {#each startJobDrivers as driver (driver.id)}
                  <div class="contact-card driver-contact-card">
                    <div class="contact-details">
                      <div class="contact-name-row">
                        <span class="contact-name">{driver.name}</span>
                        <span class="contact-badge badge-driver">
                          Truck: {driver.trucks.join(', ') || 'Heavy Vehicle'}{driver.trailerId ? ` + ${driver.trailerId}` : ''}
                        </span>
                      </div>
                      {#if driver.shifts && driver.shifts.length > 0}
                        <div class="contact-subtitle shifts-text">
                          Scheduled: {driver.shifts.join(' • ')}
                        </div>
                      {/if}
                      <div class="contact-comm-row">
                        <div class="comm-item {driver.phone ? '' : 'unavailable'}">
                          <Phone size={13} />
                          {#if driver.phone}
                            <a href="tel:{toE164(driver.phone)}" class="comm-link">{formatPhoneNumber(driver.phone)}</a>
                          {:else}
                            <span class="comm-na">No mobile on file</span>
                          {/if}
                        </div>
                      </div>
                    </div>

                    <div class="contact-channels driver-single-channel">
                      <label class="channel-checkbox-label">
                        <input
                          type="checkbox"
                          class="channel-checkbox"
                          bind:checked={driver.selected}
                        />
                        <span class="channel-text">Send Driver Briefing</span>
                      </label>
                    </div>
                  </div>
                {/each}
              </div>
            {/if}
          </div>
        {/if}

        {#if startJobError}
          <div class="alert alert-danger modal-error-alert" transition:slide>
            <WarningFilled size={16} />
            <span>{startJobError}</span>
          </div>
        {/if}
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer">
        <div class="modal-footer-stats">
          <span class="stats-label">Queued for Notification:</span>
          <span class="stat-pill">
            <Email size={12} />
            <strong>{[...startJobMainContacts, ...startJobSecondaryContacts].filter(c => c.sendEmail).length}</strong> Emails
          </span>
          <span class="stat-pill">
            <Phone size={12} />
            <strong>{[...startJobMainContacts, ...startJobSecondaryContacts].filter(c => c.sendPhone).length}</strong> SMS
          </span>
          <span class="stat-pill">
            <DeliveryTruck size={12} />
            <strong>{startJobDrivers.filter(d => d.selected).length}</strong> Drivers
          </span>
        </div>

        <div class="modal-footer-actions">
          <button
            class="btn btn-outline"
            type="button"
            on:click={handleClose}
            disabled={isStartingJob}
          >
            Cancel
          </button>
          <button
            class="btn btn-start-job btn-modal-confirm"
            type="button"
            on:click={handleConfirm}
            disabled={isStartingJob}
          >
            {#if isStartingJob}
              <span class="btn-spinner" />
              <span>Activating Job...</span>
            {:else}
              <PlayFilled size={15} />
              <span>Confirm &amp; Activate Job</span>
            {/if}
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .btn-start-job {
    background: #16a34a;
    color: #ffffff;
    border: 1px solid #15803d;
    font-weight: 700;
    letter-spacing: 0.02em;
    box-shadow: 0 1px 3px rgba(22, 163, 74, 0.25);
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    transition: all 0.15s ease;
    border-radius: 6px;
    cursor: pointer;
  }

  .btn-start-job:hover:not(:disabled) {
    background: #15803d;
    border-color: #166534;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(22, 163, 74, 0.35);
  }

  .btn-start-job:active:not(:disabled) {
    transform: translateY(0);
  }

  .btn-start-job:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-outline {
    background: transparent;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    color: var(--tt-slate-700, #334155);
    padding: 0.5rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.88rem;
    font-weight: 600;
    transition: all 0.15s ease;
  }

  .btn-outline:hover:not(:disabled) {
    background: var(--tt-slate-100, #f1f5f9);
    border-color: var(--tt-slate-400, #94a3b8);
  }

  .start-job-modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.55);
    backdrop-filter: blur(4px);
    z-index: 1400;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    pointer-events: auto;
  }

  .start-job-modal-card {
    background: #ffffff;
    border-radius: 16px;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    width: 100%;
    max-width: 820px;
    max-height: 88vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.22), 0 8px 16px rgba(0, 0, 0, 0.1);
    animation: modalScaleIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  }

  @keyframes modalScaleIn {
    from {
      opacity: 0;
      transform: scale(0.96) translateY(8px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  .modal-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 1.25rem 1.5rem;
    background: #ffffff;
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
    flex-shrink: 0;
  }

  .modal-title-group {
    display: flex;
    align-items: center;
    gap: 0.85rem;
  }

  .modal-icon-badge {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: #ecfdf5;
    color: #16a34a;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #bbf7d0;
    flex-shrink: 0;
  }

  .modal-title {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 800;
    color: var(--tt-slate-900, #0f172a);
    letter-spacing: -0.01em;
  }

  .modal-subtitle {
    margin: 0.15rem 0 0 0;
    font-size: 0.8rem;
    color: var(--tt-slate-500, #64748b);
  }

  .modal-close-icon-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: var(--tt-slate-400, #94a3b8);
    cursor: pointer;
    transition: all 0.15s ease;
    padding: 0;
  }

  .modal-close-icon-btn:hover:not(:disabled) {
    background: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-700, #334155);
  }

  .modal-scroll-body {
    flex: 1;
    overflow-y: auto;
    padding: 1.25rem 1.5rem;
    background: var(--tt-slate-50, #f8fafc);
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .modal-start-date-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.25rem;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  }

  .date-field-col {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .modal-field-label {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--tt-slate-600, #475569);
  }

  .modal-date-picker {
    padding: 0.45rem 0.75rem;
    font-size: 0.92rem;
    font-weight: 700;
    border: 1.5px solid #2563eb;
    border-radius: 8px;
    background: #ffffff;
    color: var(--tt-slate-900, #0f172a);
    outline: none;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
  }

  .modal-quick-meta {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    flex-wrap: wrap;
  }

  .quick-meta-pill {
    display: flex;
    flex-direction: column;
    padding: 0.35rem 0.75rem;
    background: var(--tt-slate-50, #f8fafc);
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
  }

  .quick-meta-pill .meta-label {
    font-size: 0.65rem;
    font-weight: 700;
    color: var(--tt-slate-400, #94a3b8);
    letter-spacing: 0.04em;
  }

  .quick-meta-pill .meta-val {
    font-size: 0.82rem;
    font-weight: 700;
    color: var(--tt-slate-800, #1e293b);
    max-width: 170px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .modal-info-note {
    display: flex;
    align-items: flex-start;
    gap: 0.65rem;
    padding: 0.75rem 1rem;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 10px;
    color: #1e40af;
    font-size: 0.8rem;
    line-height: 1.45;
  }

  :global(.note-icon) {
    color: #2563eb;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .note-subtext {
    display: block;
    font-size: 0.74rem;
    color: #3b82f6;
    margin-top: 0.2rem;
    font-style: italic;
  }

  .modal-loading-state {
    padding: 2.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    color: var(--tt-slate-500, #64748b);
    font-size: 0.85rem;
  }

  .contacts-section {
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 12px;
    padding: 1rem 1.15rem;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  }

  .contacts-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
  }

  .section-title-wrap {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  :global(.section-icon) {
    flex-shrink: 0;
  }

  :global(.main-icon) {
    color: #2563eb;
  }

  :global(.secondary-icon) {
    color: #64748b;
  }

  :global(.driver-icon) {
    color: #059669;
  }

  .section-heading {
    margin: 0;
    font-size: 0.88rem;
    font-weight: 700;
    color: var(--tt-slate-800, #1e293b);
  }

  .count-tag {
    font-size: 0.7rem;
    font-weight: 700;
    padding: 0.15rem 0.45rem;
    border-radius: 9999px;
  }

  .count-main {
    background: #dbeafe;
    color: #1d4ed8;
  }

  .count-sec {
    background: #f1f5f9;
    color: #475569;
  }

  .count-drv {
    background: #d1fae5;
    color: #047857;
  }

  .section-hint {
    font-size: 0.72rem;
    color: var(--tt-slate-400, #94a3b8);
    font-style: italic;
  }

  .empty-contacts-box {
    padding: 1rem;
    text-align: center;
    color: var(--tt-slate-400, #94a3b8);
    font-size: 0.8rem;
    font-style: italic;
    background: var(--tt-slate-50, #f8fafc);
    border-radius: 8px;
    border: 1px dashed var(--tt-slate-200, #e2e8f0);
  }

  .warning-box {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    color: #b45309;
    background: #fef3c7;
    border-color: #fde68a;
    font-style: normal;
  }

  .contacts-cards-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .contact-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.65rem 0.85rem;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    transition: background-color 0.12s ease, border-color 0.12s ease;
  }

  .contact-card:hover {
    background: var(--tt-slate-50, #f8fafc);
    border-color: var(--tt-slate-300, #cbd5e1);
  }

  .contact-details {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .contact-name-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .contact-name {
    font-size: 0.85rem;
    font-weight: 700;
    color: var(--tt-slate-900, #0f172a);
  }

  .contact-badge {
    font-size: 0.68rem;
    font-weight: 700;
    padding: 0.1rem 0.45rem;
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  .badge-created-for {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
  }

  .badge-job {
    background: #f1f5f9;
    color: #334155;
    border: 1px solid #cbd5e1;
  }

  .badge-yard {
    background: #fef3c7;
    color: #b45309;
    border: 1px solid #fde68a;
  }

  .badge-company-sec {
    background: #f8fafc;
    color: #64748b;
    border: 1px solid #e2e8f0;
  }

  .badge-yard-sec {
    background: #fffbeb;
    color: #92400e;
    border: 1px solid #fef3c7;
  }

  .badge-driver {
    background: #ecfdf5;
    color: #047857;
    border: 1px solid #a7f3d0;
  }

  .contact-subtitle {
    font-size: 0.74rem;
    color: var(--tt-slate-500, #64748b);
  }

  .shifts-text {
    font-weight: 500;
    color: #047857;
  }

  .contact-comm-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-top: 0.15rem;
    flex-wrap: wrap;
  }

  .comm-item {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.74rem;
    color: var(--tt-slate-600, #475569);
  }

  .comm-item.unavailable {
    color: var(--tt-slate-400, #94a3b8);
  }

  .comm-link {
    color: var(--tt-primary, #2563eb);
    text-decoration: none;
  }

  .comm-link:hover {
    text-decoration: underline;
  }

  .comm-na {
    color: var(--tt-slate-400, #94a3b8);
    font-style: italic;
  }

  .contact-channels {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
  }

  .driver-single-channel {
    min-width: 165px;
    justify-content: flex-end;
  }

  .channel-checkbox-label {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.3rem 0.55rem;
    background: var(--tt-slate-50, #f8fafc);
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 6px;
    cursor: pointer;
    user-select: none;
    transition: all 0.12s ease;
  }

  .channel-checkbox-label:hover:not(.disabled) {
    background: #eff6ff;
    border-color: #93c5fd;
  }

  .channel-checkbox-label.disabled {
    opacity: 0.45;
    cursor: not-allowed;
    background: #f1f5f9;
  }

  .channel-checkbox {
    width: 14px;
    height: 14px;
    accent-color: #2563eb;
    margin: 0;
    cursor: pointer;
  }

  .channel-checkbox-label.disabled .channel-checkbox {
    cursor: not-allowed;
  }

  .channel-text {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--tt-slate-700, #334155);
  }

  .modal-error-alert {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-radius: 8px;
    font-size: 0.82rem;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
  }

  .modal-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    background: #ffffff;
    border-top: 1px solid var(--tt-slate-200, #e2e8f0);
    flex-shrink: 0;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .modal-footer-stats {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .stats-label {
    font-size: 0.74rem;
    font-weight: 700;
    color: var(--tt-slate-500, #64748b);
  }

  .stat-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.74rem;
    padding: 0.2rem 0.55rem;
    border-radius: 6px;
    background: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-700, #334155);
    border: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .modal-footer-actions {
    display: flex;
    align-items: center;
    gap: 0.65rem;
  }

  .btn-modal-confirm {
    padding: 0.55rem 1.25rem;
    font-size: 0.88rem;
  }

  .spinner {
    width: 22px;
    height: 22px;
    border: 2.5px solid var(--tt-slate-200, #e2e8f0);
    border-top-color: #2563eb;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .btn-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    display: inline-block;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
</style>
